import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressEmailsubscriptionsV1EmailSubscriptionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/email-marketing/v1/email-subscriptions',
        destPath: '/v1/email-subscriptions',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/email-marketing/v1/email-subscriptions',
        destPath: '/v1/email-subscriptions',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/email-marketing/v1/email-subscriptions',
        destPath: '/v1/email-subscriptions',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/email-marketing/v1/email-subscriptions',
        destPath: '/v1/email-subscriptions',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/email-marketing/v1/email-subscriptions',
        destPath: '/v1/email-subscriptions',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/email-marketing/v1/email-subscriptions',
        destPath: '/v1/email-subscriptions',
      },
      {
        srcPath: '/email-marketing/v1/email-subscriptions/unsubscribe-link',
        destPath: '/v1/email-subscriptions/unsubscribe-link',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_email-subscriptions_email-subscriptions';

/** Get Email Subscription */
export function getEmailSubscription(
  payload: object
): RequestOptionsFactory<any> {
  function __getEmailSubscription({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.emailsubscriptions.v1.emailsubscription',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.GetEmailSubscription',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressEmailsubscriptionsV1EmailSubscriptionsServiceUrl({
        protoPath: '/v1/email-subscriptions/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'emailSubscription.createdDate' },
              { path: 'emailSubscription.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getEmailSubscription;
}

/**
 * Retrieves email subscriptions,
 * given the provided paging, filtering, and sorting.
 *
 * Currently, querying is supported on the `email` field
 * with the `$in` array filter.
 * For example, to query for emails `"me@my.com"` and `"you@your.org"`,
 * the filter should be formed like this:
 *
 * ```json
 * { "filter": {
 * "email": {
 * "$in": ["me@my.com", "you@your.org"]
 * }
 * }
 * }
 * ```
 *
 * To learn how to query email subscriptions, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
 */
export function queryEmailSubscriptions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryEmailSubscriptions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.emailsubscriptions.v1.emailsubscription',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.QueryEmailSubscriptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressEmailsubscriptionsV1EmailSubscriptionsServiceUrl({
        protoPath: '/v1/email-subscriptions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'subscriptions.createdDate' },
              { path: 'subscriptions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryEmailSubscriptions;
}

/**
 * Updates or creates an email subscription for the requested email.
 *
 * An email subscription is returned in the response
 * whether it was updated or created.
 */
export function upsertEmailSubscription(
  payload: object
): RequestOptionsFactory<any> {
  function __upsertEmailSubscription({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'subscription.createdDate' },
          { path: 'subscription.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.emailsubscriptions.v1.emailsubscription',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.UpsertEmailSubscription',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressEmailsubscriptionsV1EmailSubscriptionsServiceUrl({
        protoPath: '/v1/email-subscriptions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'subscription.createdDate' },
              { path: 'subscription.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __upsertEmailSubscription;
}

/** Updates or creates multiple email subscriptions. */
export function bulkUpsertEmailSubscription(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpsertEmailSubscription({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'subscriptions.createdDate' },
          { path: 'subscriptions.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.emailsubscriptions.v1.emailsubscription',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.BulkUpsertEmailSubscription',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressEmailsubscriptionsV1EmailSubscriptionsServiceUrl({
        protoPath: '/v1/email-subscriptions/bulk',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.emailSubscription.createdDate' },
              { path: 'results.emailSubscription.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpsertEmailSubscription;
}

/**
 * Creates an unsubscribe link to be shared with the relevant recipient.
 *
 * If someone clicks the **Unsubscribe** button on the confirmation page,
 * the recipient's `subscriptionStatus` is changed to `UNSUBSCRIBED`.
 */
export function generateUnsubscribeLink(
  payload: object
): RequestOptionsFactory<any> {
  function __generateUnsubscribeLink({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.emailsubscriptions.v1.emailsubscription',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.GenerateUnsubscribeLink',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressEmailsubscriptionsV1EmailSubscriptionsServiceUrl({
        protoPath: '/v1/email-subscriptions/unsubscribe-link',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __generateUnsubscribeLink;
}
